/**
 * Runtime Error Handler for comprehensive browser error logging
 * Captures and reports all JavaScript errors, unhandled promises, and resource failures
 */

interface ErrorData {
  message: string
  source?: string
  line?: number
  column?: number
  stack?: string
  userAgent: string
  url: string
  timestamp: number
  type: 'javascript' | 'promise' | 'resource' | 'console' | 'unhandledrejection' | 'warning' | 'deprecated'
}

class RuntimeErrorHandler {
  private isInitialized = false
  private errorEndpoint = '/__runtime-error'
  private errorQueue: ErrorData[] = []
  private isOnline = navigator.onLine

  constructor() {
    this.setupNetworkListeners()
  }

  /**
   * Initialize all error handlers
   */
  init() {
    if (this.isInitialized || typeof window === 'undefined') {
      return
    }

    console.log('🔧 Initializing runtime error handler...')

    // Process early error queue first
    this.processEarlyErrorQueue()

    this.setupJavaScriptErrorHandler()
    this.setupPromiseRejectionHandler()
    this.setupResourceErrorHandler()
    this.setupConsoleErrorCapture()
    this.setupConsoleWarningCapture()
    this.setupUnhandledRejectionHandler()
    this.setupDeprecatedAPIWarning()

    this.isInitialized = true
    console.log('✅ Runtime error handler initialized')

    // Process any queued errors
    this.processErrorQueue()
  }

  /**
   * Process errors captured before the main handler was loaded
   */
  private processEarlyErrorQueue() {
    if ((window as any).__ERROR_QUEUE && Array.isArray((window as any).__ERROR_QUEUE)) {
      const earlyErrors = (window as any).__ERROR_QUEUE
      console.log(`📋 Processing ${earlyErrors.length} early-captured errors...`)

      earlyErrors.forEach((errorData: any) => {
        console.error('⏰ Early Error:', {
          type: errorData.type,
          message: errorData.message,
          source: errorData.source,
          line: errorData.line,
          column: errorData.column
        })

        this.logError(errorData)
      })

      // Clear the early error queue
      ;(window as any).__ERROR_QUEUE = []
    }
  }

  /**
   * Handle JavaScript errors (window.onerror)
   */
  private setupJavaScriptErrorHandler() {
    window.addEventListener('error', (event) => {
      const errorData: ErrorData = {
        message: event.message,
        source: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: Date.now(),
        type: 'javascript'
      }

      console.error('💥 JavaScript Error:', {
        message: event.message,
        source: `${event.filename}:${event.lineno}:${event.colno}`,
        stack: event.error?.stack
      })

      this.logError(errorData)
    })
  }

  /**
   * Handle unhandled promise rejections
   */
  private setupPromiseRejectionHandler() {
    window.addEventListener('unhandledrejection', (event) => {
      const errorData: ErrorData = {
        message: `Unhandled Promise Rejection: ${event.reason}`,
        source: window.location.href,
        stack: event.reason?.stack || String(event.reason),
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: Date.now(),
        type: 'promise'
      }

      console.error('🔴 Unhandled Promise Rejection:', {
        reason: event.reason,
        stack: event.reason?.stack
      })

      this.logError(errorData)

      // Prevent the default browser behavior
      event.preventDefault()
    })
  }

  /**
   * Handle resource loading errors (images, scripts, etc.)
   */
  private setupResourceErrorHandler() {
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const element = event.target as HTMLElement
        const errorData: ErrorData = {
          message: `Failed to load resource: ${element.tagName}`,
          source: (element as any).src || (element as any).href || window.location.href,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: Date.now(),
          type: 'resource'
        }

        console.error('📦 Resource Loading Error:', {
          element: element.tagName,
          source: (element as any).src || (element as any).href
        })

        this.logError(errorData)
      }
    }, true) // Use capture phase
  }

  /**
   * Capture console.error calls
   */
  private setupConsoleErrorCapture() {
    const originalError = console.error
    console.error = (...args: any[]) => {
      // Call original console.error
      originalError.apply(console, args)

      // Log to our error handler
      const errorData: ErrorData = {
        message: args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' '),
        source: window.location.href,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: Date.now(),
        type: 'console'
      }

      this.logError(errorData)
    }
  }

  /**
   * Handle unhandled rejections (legacy support)
   */
  private setupUnhandledRejectionHandler() {
    // Some older browsers might fire this instead of unhandledrejection
    window.addEventListener('error', (event) => {
      if (event.error && event.error instanceof Error && !event.filename) {
        const errorData: ErrorData = {
          message: `Unhandled Error: ${event.error.message}`,
          source: window.location.href,
          stack: event.error.stack,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: Date.now(),
          type: 'unhandledrejection'
        }

        console.error('⚠️ Unhandled Error:', event.error)
        this.logError(errorData)
      }
    })
  }

  /**
   * Capture console.warning calls
   */
  private setupConsoleWarningCapture() {
    const originalWarn = console.warn
    console.warn = (...args: any[]) => {
      // Call original console.warn
      originalWarn.apply(console, args)

      // Log to our error handler
      const errorData: ErrorData = {
        message: args.map(arg =>
          typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
        ).join(' '),
        source: window.location.href,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: Date.now(),
        type: 'warning'
      }

      this.logError(errorData)
    }
  }

  /**
   * Capture deprecated API usage warnings
   */
  private setupDeprecatedAPIWarning() {
    // Monitor for deprecated API usage
    const originalWarn = console.warn
    console.warn = (...args: any[]) => {
      originalWarn.apply(console, args)

      const message = args.join(' ')
      if (message.includes('deprecated') || message.includes('legacy')) {
        const errorData: ErrorData = {
          message: `Deprecated API Warning: ${message}`,
          source: window.location.href,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: Date.now(),
          type: 'deprecated'
        }

        this.logError(errorData)
      }
    }
  }

  /**
   * Setup network status listeners
   */
  private setupNetworkListeners() {
    window.addEventListener('online', () => {
      this.isOnline = true
      console.log('🌐 Network restored - processing error queue...')
      this.processErrorQueue()
    })

    window.addEventListener('offline', () => {
      this.isOnline = false
      console.log('📵 Network offline - errors will be queued')
    })
  }

  /**
   * Send error to server endpoint
   */
  private async logError(errorData: ErrorData) {
    if (!this.isOnline) {
      this.errorQueue.push(errorData)
      return
    }

    try {
      await fetch(this.errorEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorData)
      })
    } catch (error) {
      console.warn('⚠️ Failed to send error to server, queuing for later:', error)
      this.errorQueue.push(errorData)
    }
  }

  /**
   * Process queued errors when connection is restored
   */
  private async processErrorQueue() {
    if (this.errorQueue.length === 0 || !this.isOnline) {
      return
    }

    console.log(`📤 Processing ${this.errorQueue.length} queued errors...`)

    const errorsToProcess = [...this.errorQueue]
    this.errorQueue = []

    for (const errorData of errorsToProcess) {
      try {
        await this.logError(errorData)
        // Small delay to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100))
      } catch (error) {
        console.warn('⚠️ Failed to process queued error:', error)
        // Re-queue failed errors
        this.errorQueue.push(errorData)
      }
    }

    if (this.errorQueue.length > 0) {
      console.warn(`⚠️ ${this.errorQueue.length} errors remain in queue`)
    }
  }

  /**
   * Get current error statistics
   */
  getStats() {
    return {
      isInitialized: this.isInitialized,
      isOnline: this.isOnline,
      queuedErrors: this.errorQueue.length,
      endpoint: this.errorEndpoint
    }
  }

  /**
   * Manually log an error
   */
  logCustomError(message: string, type: ErrorData['type'] = 'javascript', extra?: any) {
    const errorData: ErrorData = {
      message,
      source: window.location.href,
      stack: extra?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: Date.now(),
      type
    }

    console.error(`🔧 Custom Error (${type}):`, { message, extra })
    this.logError(errorData)
  }
}

// Create global instance
export const runtimeErrorHandler = new RuntimeErrorHandler()

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  // Initialize immediately for maximum coverage
  runtimeErrorHandler.init()

  // Also expose to global scope for debugging
  ;(window as any).__runtimeErrorHandler = runtimeErrorHandler
}

export default runtimeErrorHandler